<template>
  <div class="planning-login-view">
    <!-- Overlay de chargement avec option d'annulation -->
    <div v-if="loading" id="loading-overlay" class="overlay active">
      <div class="overlay-text">
        Identification en cours<span class="dots"></span>
      </div>
    </div>

    <!-- Container principal -->
    <div class="login-container">
      <h2>EVA Identification</h2>

      <!-- Contenu du formulaire -->
      <div class="login-form-wrapper">
        <!-- Message d'erreur -->
        <div v-if="error" class="alert alert-danger">
          {{ error }}
          <button class="close" @click="clearError">&times;</button>
        </div>

        <!-- Formulaire -->
        <form style="margin-top: 15%" @submit.prevent="handleLogin">
          <table class="form-table">
            <tbody>
            <tr>
              <td style="width: 25%"></td>
              <td style="text-align: right; color: #4a5568; font-size: 0.875rem;font-weight: 500">
                <span>Identifiant</span>
              </td>
              <td>
                <input
                    id="email"
                    v-model="loginForm.email"
                    :disabled="loading"
                    autocomplete="email"
                    name="email"
                    placeholder="Votre adresse email"
                    required
                    type="text"
                />
              </td>
            </tr>
            <tr>
              <td style="width: 25%"></td>
              <td style="text-align: right; color: #4a5568;font-size: 0.875rem;font-weight: 500">
                <span>Mot de passe</span>
              </td>
              <td>
                <input
                    id="password"
                    v-model="loginForm.password"
                    :disabled="loading"
                    autocomplete="current-password"
                    name="password"
                    placeholder="Votre mot de passe"
                    required
                    type="password"
                />
              </td>
            </tr>
            <tr>
              <td colspan="3" style="text-align: center; padding-top: 1.5rem;">
                <input
                    :disabled="loading"
                    :value="loading ? 'Connexion...' : 'Valider l\'identification'"
                    type="submit"
                />
              </td>
            </tr>
            <tr>
              <td colspan="3" style="text-align: center;">
                <a class="azure-login-link btn-azure" href="#" @click.prevent="handleAzureLogin">
                  <img alt="" class="apform-icon" src="https://api.apform.fr/images/collaborateurs-apform.png" style="margin-right: 8px;"/>
                  Collaborateurs Apform
                </a>
              </td>
            </tr>
            </tbody>
          </table>
        </form>


        <!-- Séparateur -->
        <hr/>

        <!-- Aide -->
        <div class="help-section">
          <p style="color: #4a5568; font-size: 0.875rem; text-align: center; margin: 0;">
            <a :href="forgetPasswordUrl" style="color: #3182ce; text-decoration: none;" target="_blank">Mot de passe oublié ?</a>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {onMounted, onUnmounted, ref} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {usePlanningStore} from '@/store/planningStore'
import {storeToRefs} from 'pinia'

import { planningReturnTo } from '@/router/planning/returnTo'

const router = useRouter()
const route = useRoute()
const returnTo = () => planningReturnTo(route.query.returnTo)
const planningStore = usePlanningStore()
const {loading, error} = storeToRefs(planningStore)

// État local
const loginForm = ref({
  email: '',
  password: ''
})


const forgetPasswordUrl = `${process.env.VUE_APP_API_URL}/eva/forgotPassword`

// Gestion de la connexion
const handleLogin = async () => {
  planningStore.login(loginForm.value.email, loginForm.value.password).then(() => {
    router.replace(returnTo())
  }).catch((err) => {
    console.warn('Erreur de connexion:', err)
  })
}

// Nettoyage des erreurs
const clearError = () => {
  planningStore.clearError()
}


// Gestion de la connexion Azure
const handleAzureLogin = () => {
  planningStore.getAzureAuthUrl(returnTo()).then((url) => {
    window.open(url, '_self')
  }).catch((err) => {
    console.error('Erreur lors de la connexion Azure:', err)
  })
}


onUnmounted(() => {
  document.getElementsByClassName('container')[0].removeAttribute('style');
});


// Vérification si l'utilisateur est déjà connecté ou a des credentials
onMounted(() => {
  const link = document.createElement('link');
  link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';
  link.rel = 'stylesheet';
  document.head.appendChild(link);

  document.getElementsByClassName('container')[0].style.maxWidth = '100%';

  const urlParams = new URLSearchParams(window.location.search)


  const azureToken = urlParams.get('token')
  const authenticatedUserAccountObjId = urlParams.get('authenticatedUserAccountObjId');
  if (azureToken && authenticatedUserAccountObjId) {
    planningStore.handleAzureCallback(azureToken, authenticatedUserAccountObjId)
      .then(() => router.replace(returnTo()))
      .catch(() => { error.value = 'La connexion a échoué. Réessayez.' })
  } else if (urlParams.get('error')) {
    error.value = 'La connexion a échoué. Réessayez.'
  }

})
</script>


<style scoped>

html {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%) no-repeat center center fixed;
}

body {
  background-color: transparent !important;
}


.planning-login-view {

  font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  margin: 0;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
}

.login-container {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 1200px;
  height: 600px;
  display: flex;
  flex-direction: row;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
  margin: auto;
  position: relative;
  animation: fadeIn 0.5s ease-out;
}

.login-container::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  width: 50%;
  height: 100%;
  background: url('https://api.apform.fr/images/accueil.jpg') no-repeat center;
  background-size: cover;
  z-index: 0;
}

.login-container::after {
  content: 'EVA Identification';
  position: absolute;
  left: 0;
  top: 0;
  width: 50%;
  height: 100%;
  background: rgba(49, 130, 206, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2.5rem;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  text-align: center;
  z-index: 1;
}

.login-container h2 {
  display: none;
}

.login-form-wrapper {
  width: 50%;
  margin-left: 50%;
  padding: 3rem;
  position: relative;
  z-index: 2;
}

.form-table {
  width: 100%;
  border-collapse: collapse;
  background: transparent;
}

.form-table td {
  padding: 0.5rem;
  vertical-align: middle;
}

.form-table font[color="#0000AA"] {
  color: #4a5568;
  font-size: 0.875rem;
  font-weight: 500;
  display: block;
  margin-bottom: 0.5rem;
}

.form-table input[type="text"],
.form-table input[type="password"] {
  width: 100%;
  max-width: 100%;
  padding: 0.875rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  background: #fff;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.form-table input[type="text"]:focus,
.form-table input[type="password"]:focus {
  border-color: #3182ce;
  box-shadow: 0 0 0 3px rgba(49, 130, 206, 0.2);
  outline: none;
}

.form-table input[type="submit"] {
  width: 100%;
  padding: 0.875rem;
  background: linear-gradient(90deg, #3182ce 0%, #2b6cb0 100%);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.form-table input[type="submit"]:hover:not(:disabled) {
  background: linear-gradient(90deg, #2b6cb0 0%, #2c5282 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(49, 130, 206, 0.2);
}

.form-table input[type="submit"]:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.azure-login-link {
  display: block;
  width: 100%;
  text-decoration: none;
  transition: all 0.3s ease;
  text-align: center;
}

.azure-login-link.btn-azure {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 52px;
  gap: 0.5rem;
  padding: 0.30rem;
  background: #ffffff;
  border: 1.5px solid #4a8db3;
  border-radius: 8px;
  color: #4a8db3;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.azure-login-link.btn-azure .apform-icon {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: inline-block;
}

.azure-login-link.btn-azure:hover {
  border-color: #3a7aa0;
  color: #3a7aa0;
  box-shadow: 0 3px 10px rgba(74, 141, 179, 0.2);
  transform: translateY(-1px);
}

hr {
  border: 0;
  height: 1px;
  background: linear-gradient(to right, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0));
  margin: 1.5rem 0;
}

.alert {
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  position: relative;
}

.alert-danger {
  background-color: #fed7d7;
  color: #c53030;
  border: 1px solid #feb2b2;
}

.alert .close {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  color: inherit;
  opacity: 0.7;
}

.alert .close:hover {
  opacity: 1;
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  visibility: hidden;
}

.overlay.active {
  visibility: visible;
}

.overlay-text {
  color: white;
  font-size: 2rem;
  font-weight: 600;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.overlay-text .dots::after {
  content: '...';
  display: inline-block;
  animation: blink 1.5s infinite;
}

.overlay-cancel a {
  color: #f59e0b;
  font-size: 1.2rem;
  font-weight: 500;
  text-decoration: none;
  padding: 0.5rem 1rem;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.2);
}

.overlay-cancel a:hover {
  background: #f59e0b;
  color: white;
  text-decoration: none;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes blink {
  0% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

@media (max-width: 768px) {
  .login-container {
    flex-direction: column;
    height: auto;
    width: 95%;
    margin: 1rem;
  }

  .login-container::before,
  .login-container::after {
    width: 100%;
    height: 300px;
  }

  .login-form-wrapper {
    width: 100%;
    margin-left: 0;
    padding: 2rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .login-container {
    animation: none;
  }

  .overlay-text .dots::after {
    animation: none;
  }
}
</style>
